<template>
  <teleport to="body">
    <div v-if="visible" :class="ns.b()">
      <div :class="ns.e('mask')" @click="emit('close')" />
      <div
        ref="menuRef"
        :class="ns.e('menu')"
        role="menu"
        :aria-label="ariaLabel"
        :style="menuStyle"
        @click.stop
      >
        <div :class="ns.e('list')" @scroll="openSubKey = null">
          <template v-if="items.length">
            <template v-for="(item, index) in items" :key="item.key">
              <div
                v-if="index > 0 && shouldDivide(item, items[index - 1]!)"
                :class="ns.e('divider')"
                role="separator"
              />
              <div
                :class="ns.e('item-wrap')"
                @mouseenter="onItemEnter(item, $event)"
                @mouseleave="openSubKey = null"
              >
                <button
                  type="button"
                  :class="[
                    ns.e('item'),
                    ns.is('disabled', !!item.disabled),
                    ns.is('open', openSubKey === item.key),
                    ns.is('has-children', hasChildren(item)),
                  ]"
                  role="menuitem"
                  :disabled="item.disabled"
                  @click="onItemClick(item, $event)"
                >
                  <span :class="ns.e('icon')">
                    <slot name="icon" :item="item" />
                  </span>
                  <span :class="ns.e('label')">{{ item.label }}</span>
                  <span v-if="item.tag" :class="ns.e('tag')">{{ item.tag }}</span>
                  <span v-if="hasChildren(item)" :class="ns.e('caret')">›</span>
                </button>
                <div
                  v-if="hasChildren(item) && openSubKey === item.key"
                  :class="ns.e('submenu')"
                  role="menu"
                  :style="submenuStyle"
                  @click.stop
                >
                  <button
                    v-for="child in item.children"
                    :key="child.key"
                    type="button"
                    :class="[ns.e('item'), ns.is('disabled', !!child.disabled)]"
                    role="menuitem"
                    :disabled="child.disabled"
                    @click="navigate(child)"
                  >
                    <span :class="ns.e('icon')">
                      <slot name="icon" :item="child" />
                    </span>
                    <span :class="ns.e('label')">{{ child.label }}</span>
                    <span v-if="child.tag" :class="ns.e('tag')">{{ child.tag }}</span>
                  </button>
                </div>
              </div>
            </template>
          </template>
          <div v-else :class="ns.e('empty')">{{ emptyText }}</div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts" setup>
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import type { AppDomainMenuAnchor, AppDomainMenuItem } from './types'

defineOptions({
  name: 'ZAppDomainMenu',
})

const props = withDefaults(
  defineProps<{
    visible?: boolean
    items?: AppDomainMenuItem[]
    anchor?: AppDomainMenuAnchor
    emptyText?: string
    ariaLabel?: string
  }>(),
  {
    visible: false,
    items: () => [],
    anchor: () => ({ top: 0, left: 0 }),
    emptyText: '暂无可用功能',
    ariaLabel: '二级菜单',
  }
)

const emit = defineEmits<{
  close: []
  select: [item: AppDomainMenuItem]
}>()

const ns = useNamespace('app-domain-menu')
const menuRef = ref<HTMLElement>()
const openSubKey = ref<string | null>(null)
const adjustedLeft = ref(0)
const subAnchor = ref({ top: 0, left: 0 })

const menuStyle = computed(() => ({
  top: `${props.anchor.top}px`,
  left: `${adjustedLeft.value || props.anchor.left}px`,
}))

const submenuStyle = computed(() => ({
  top: `${subAnchor.value.top}px`,
  left: `${subAnchor.value.left}px`,
}))

function hasChildren(item: AppDomainMenuItem) {
  return Boolean(item.children?.length)
}

function shouldDivide(current: AppDomainMenuItem, prev: AppDomainMenuItem) {
  return hasChildren(current) !== hasChildren(prev)
}

function placeSubmenu(el: HTMLElement) {
  const wrap = el.closest(`.${ns.e('item-wrap')}`) as HTMLElement | null
  const rect = (wrap ?? el).getBoundingClientRect()
  subAnchor.value = {
    top: Math.round(rect.top - 4),
    left: Math.round(rect.right - 2),
  }
}

function onItemEnter(item: AppDomainMenuItem, event: MouseEvent) {
  if (!hasChildren(item)) {
    openSubKey.value = null
    return
  }
  placeSubmenu(event.currentTarget as HTMLElement)
  openSubKey.value = item.key
}

function onItemClick(item: AppDomainMenuItem, event: MouseEvent) {
  if (item.disabled) return
  if (hasChildren(item)) {
    if (openSubKey.value === item.key) {
      openSubKey.value = null
      return
    }
    placeSubmenu(event.currentTarget as HTMLElement)
    openSubKey.value = item.key
    return
  }
  navigate(item)
}

function navigate(item: AppDomainMenuItem) {
  if (item.disabled) return
  emit('select', item)
  emit('close')
}

async function clampPosition() {
  adjustedLeft.value = props.anchor.left
  await nextTick()
  const el = menuRef.value
  if (!el) return
  const padding = 8
  let left = props.anchor.left
  const rect = el.getBoundingClientRect()
  if (left + rect.width > window.innerWidth - padding) {
    left = Math.max(padding, window.innerWidth - rect.width - padding)
  }
  if (left < padding) left = padding
  adjustedLeft.value = left
}

function onKeydown(event: KeyboardEvent) {
  if (!props.visible) return
  if (event.key === 'Escape') {
    event.preventDefault()
    emit('close')
  }
}

watch(
  () => [props.visible, props.anchor.left, props.anchor.top] as const,
  async ([open]) => {
    openSubKey.value = null
    if (!open) return
    await clampPosition()
  }
)

watch(
  () => props.visible,
  (open) => {
    if (open) {
      window.addEventListener('keydown', onKeydown)
      window.addEventListener('resize', clampPosition)
    } else {
      window.removeEventListener('keydown', onKeydown)
      window.removeEventListener('resize', clampPosition)
    }
  }
)

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', clampPosition)
})
</script>
